import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccountControllerOnboardingQuery } from '@/services/generated/api';
import { PageSkeleton } from '@/components/ui/page-skeleton';

/**
 * Holds the product shut until the entry sequence is finished.
 *
 * ## Why this exists rather than three buttons removed
 *
 * The three screens used to offer a way past themselves, and taking those
 * buttons away only takes away the button: `/get-started` is still a URL
 * somebody can type, and the SPA would have rendered it. The steps are only
 * mandatory if there is nowhere else to go, and that is what this does — it
 * wraps everything inside `AppLayout`, so every route of the product passes
 * through it.
 *
 * ## What it deliberately is not
 *
 * It is not authorisation. The backend does not 403 the data plane over an
 * unfinished sequence, and it should not: the sequence is about getting
 * somebody through the front door, and a half-finished sign-up whose every API
 * call failed would be a far worse experience than being shown the step it is
 * waiting for. The server decides WHICH step (`GET /api/account/onboarding`);
 * this component only goes there.
 *
 * ## Who it lets straight through
 *
 * Anybody the server says owes nothing — which includes every customer who
 * existed before the sequence did. That is decided there, not here, and for a
 * reason worth keeping: their verification columns are empty because the
 * columns are younger than their accounts, and a rule written in the client
 * would have locked them all out of a product they already pay for.
 *
 * While the answer is in flight it renders a `PageSkeleton` rather than the
 * product. Flashing the dashboard for one frame and then yanking it away is
 * worse than a placeholder, and it would also mean the screens behind it briefly
 * fetched. The skeleton keeps the content area's frame, and announces the load.
 */

const STEP_PATHS: Record<string, string> = {
  'verify-email': '/verify-email',
  'verify-phone': '/verify-phone',
  segment: '/segment',
};

export function OnboardingGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const location = useLocation();
  const { data, isLoading, isError } = useAccountControllerOnboardingQuery();

  // A failed read must not become a locked door. The sequence is a funnel, not
  // a permission: if we cannot tell what is owed, the product is the safer
  // place to be — the screens themselves still know their own state.
  if (isError) return <>{children}</>;
  if (isLoading || !data) return <PageSkeleton />;

  const target = data.next ? STEP_PATHS[data.next] : null;
  if (target && location.pathname !== target) {
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}
